import { z } from "zod";
import { WeekDay } from "@/types/amenity";
import i18n from "@/i18n";

const getValidationMessages = () => ({
  selectAtLeastOneDay: i18n.t("amenities.validation.selectAtLeastOneDay"),
  startTimeRequired: i18n.t("amenities.validation.startTimeRequired"),
  endTimeRequired: i18n.t("amenities.validation.endTimeRequired"),
  startEarlierThanEnd: i18n.t("amenities.validation.startEarlierThanEnd"),
  nameRequired: i18n.t("amenities.validation.nameRequired"),
  categoryRequired: i18n.t("amenities.validation.categoryRequired"),
  descriptionRequired: i18n.t("amenities.validation.descriptionRequired"),
  addAtLeastOneSchedule: i18n.t("amenities.validation.addAtLeastOneSchedule"),
});

export const createScheduleSchema = () =>
  z
    .object({
      days: z
        .array(z.enum(WeekDay))
        .min(1, { message: getValidationMessages().selectAtLeastOneDay }),
      startTime: z
        .string()
        .min(1, { message: getValidationMessages().startTimeRequired }),
      endTime: z
        .string()
        .min(1, { message: getValidationMessages().endTimeRequired }),
    })
    .refine(
      (val) => {
        // both in HH:mm
        if (!val.startTime || !val.endTime) return false;
        const [sh, sm] = val.startTime.split(":").map(Number);
        const [eh, em] = val.endTime.split(":").map(Number);
        if ([sh, sm, eh, em].some((n) => Number.isNaN(n))) return false;
        const start = sh * 60 + sm;
        const end = eh * 60 + em;
        return start < end;
      },
      {
        message: getValidationMessages().startEarlierThanEnd,
        path: ["endTime"],
      },
    );

export const createAmenityFormSchema = () =>
  z.object({
    name: z
      .string()
      .min(1, { message: getValidationMessages().nameRequired })
      .regex(/^[\p{L}\p{N}\s.,'-]+$/u, "Invalid Input"),
    description: z
      .string()
      .min(1, { message: getValidationMessages().descriptionRequired }),
    category: z
      .string()
      .trim()
      .min(1, { message: getValidationMessages().categoryRequired }),
    isActive: z.boolean().default(true),
    photoId: z.string().optional(),
    serviceIds: z.array(z.string()).default([]),
    schedules: z
      .array(createScheduleSchema())
      .min(1, { message: getValidationMessages().addAtLeastOneSchedule }),
  });

export type AmenityFormSchema = ReturnType<typeof createAmenityFormSchema>;
export type AmenityFormData = z.input<AmenityFormSchema>;
